import { authService } from './authService';
import { PermissionType } from '@/types/permissions';

class PermissionService {
  private isInitialized = false;
  private isSystemAdmin = false;
  private userPermissions = new Set<PermissionType>();
  private userStandorte: Array<{ nr: string; name: string }> = [];

  async initAsync(): Promise<void> {
    this.userStandorte = [];
    this.userPermissions.clear();

    try {
      this.isSystemAdmin = authService.isSystemAdmin();

      authService.getBerechtigungen().forEach((x) => this.userPermissions.add(x.typ as PermissionType));
      authService
        .getStandorte()
        .forEach((x) => this.userStandorte.push({ nr: x.nr, name: x.name || '' }));

      this.isInitialized = true;
    } catch (error) {
      // ignore...
      this.isInitialized = true;
    }
  }

  getStandorte(): Array<{ nr: string; name: string }> {
    return this.userStandorte;
  }

  hasPermission(permission: PermissionType): boolean {
    if (this.isSystemAdmin) {
      return true;
    }
    return this.userPermissions.has(permission);
  }

  hasAnyPermission(permissions: PermissionType[]): boolean {
    if (this.isSystemAdmin) {
      return true;
    }
    return permissions.some((x) => this.userPermissions.has(x));
  }

  hasAllPermissions(permissions: PermissionType[]): boolean {
    if (this.isSystemAdmin) {
      return true;
    }
    return permissions.every((x) => this.userPermissions.has(x));
  }

  initialized(): boolean {
    return this.isInitialized;
  }

  clear(): void {
    this.isInitialized = false;
    this.isSystemAdmin = false;
    this.userPermissions.clear();
  }

  canViewProdukte(): boolean {
    return this.hasAnyPermission([PermissionType.ProduktRead, PermissionType.ProduktWrite]);
  }

  canEditProdukte(): boolean {
    return this.hasPermission(PermissionType.ProduktWrite);
  }

  canViewStandorte(): boolean {
    return this.hasAnyPermission([PermissionType.StandortRead, PermissionType.StandortWrite]);
  }

  canEditStandorte(): boolean {
    return this.hasPermission(PermissionType.StandortWrite);
  }

  canViewPartner(): boolean {
    return this.hasAnyPermission([PermissionType.PartnerRead, PermissionType.PartnerWrite]);
  }

  canEditPartner(): boolean {
    return this.hasPermission(PermissionType.PartnerWrite);
  }

  canViewAbholungen(): boolean {
    return this.hasAnyPermission([PermissionType.AbholungRead, PermissionType.AbholungWrite]);
  }

  canEditAbholungen(): boolean {
    return this.hasPermission(PermissionType.AbholungWrite);
  }

  canViewEinlieferungen(): boolean {
    return this.hasAnyPermission([PermissionType.EinlieferungRead, PermissionType.EinlieferungWrite]);
  }

  canEditEinlieferungen(): boolean {
    return this.hasPermission(PermissionType.EinlieferungWrite);
  }

  canViewAblieferungen(): boolean {
    return this.hasAnyPermission([PermissionType.AblieferungRead, PermissionType.AblieferungWrite]);
  }

  canEditAblieferungen(): boolean {
    return this.hasPermission(PermissionType.AblieferungWrite);
  }

  canViewBestaende(): boolean {
    return this.hasAnyPermission([PermissionType.BestandRead, PermissionType.BestandWrite]);
  }

  canEditBestaende(): boolean {
    return this.hasPermission(PermissionType.BestandWrite);
  }

  canViewNachweise(): boolean {
    return this.hasPermission(PermissionType.NachweisRead);
  }

  canViewNachweisverfolgung(): boolean {
    return this.hasPermission(PermissionType.Nachweisverfolgung);
  }

  canAccessCockpit(): boolean {
    return this.hasPermission(PermissionType.Cockpit);
  }

  canAccessReports(): boolean {
    return this.hasPermission(PermissionType.Report);
  }

  canAccessYtdReport(): boolean {
    return this.hasPermission(PermissionType.YtdReport);
  }

  canAccessGenericImport(): boolean {
    return this.hasPermission(PermissionType.GenericImport);
  }

  canImportInbound(): boolean {
    return this.hasPermission(PermissionType.ImportInbound);
  }

  canImportOutbound(): boolean {
    return this.hasPermission(PermissionType.ImportOutbound);
  }

  canViewZertifikate(): boolean {
    return this.hasPermission(PermissionType.ZertifikatRead);
  }

  canEditZertifikate(): boolean {
    return this.hasPermission(PermissionType.ZertifikatWrite);
  }

  canViewBilanzen(): boolean {
    return this.hasPermission(PermissionType.BilanzRead);
  }

  canEditBilanzen(): boolean {
    return this.hasPermission(PermissionType.BilanzWrite);
  }

  canViewPocs(): boolean {
    return this.hasPermission(PermissionType.PocRead);
  }

  canAccessNachweisverteilung(): boolean {
    return this.hasPermission(PermissionType.Nachweisverteilung);
  }

  canViewMandantAllgemein(): boolean {
    return this.hasAnyPermission([PermissionType.MandantAllgemeinRead, PermissionType.MandantAllgemeinWrite]);
  }

  canEditMandantAllgemein(): boolean {
    return this.hasPermission(PermissionType.MandantAllgemeinWrite);
  }

  canViewZertifizierungen(): boolean {
    return this.hasAnyPermission([
      PermissionType.ZertifizierungenRead,
      PermissionType.ZertifizierungenWrite
    ]);
  }

  canEditZertifizierungen(): boolean {
    return this.hasPermission(PermissionType.ZertifizierungenWrite);
  }

  // Router Guard Methoden
  canAccessRoute(routeName: string): boolean {
    if (this.isSystemAdmin) {
      return true;
    }

    // Route-spezifische Berechtigungsprüfung
    const routePermissions: Record<string, PermissionType[]> = {
      // Bewegungsdaten
      abholungen: [PermissionType.AbholungRead, PermissionType.AbholungWrite],
      'abholungen-edit': [PermissionType.AbholungRead, PermissionType.AbholungWrite],
      einlieferungen: [PermissionType.EinlieferungRead, PermissionType.EinlieferungWrite],
      'einlieferungen-edit': [PermissionType.EinlieferungRead, PermissionType.EinlieferungWrite],
      ablieferungen: [PermissionType.AblieferungRead, PermissionType.AblieferungWrite],
      'ablieferungen-edit': [PermissionType.AblieferungRead, PermissionType.AblieferungWrite],
      bestaende: [PermissionType.BestandRead, PermissionType.BestandWrite],
      'bestaende-edit': [PermissionType.BestandRead, PermissionType.BestandWrite],
      'bestaende-create': [PermissionType.BestandWrite],
      'tankstelle-bestand': [PermissionType.Cockpit],
      nachweisverfolgung: [PermissionType.Nachweisverfolgung],
      'nachweisverfolgung-detail': [PermissionType.Nachweisverfolgung],
      nachweisbescheinigungen: [PermissionType.NachweisRead],
      'nachweisbescheinigungen-detail': [PermissionType.NachweisRead],
      nachweise: [PermissionType.ZertifikatRead, PermissionType.ZertifikatWrite],
      'nachweise-edit': [PermissionType.ZertifikatRead, PermissionType.ZertifikatWrite],
      bilanzen: [PermissionType.BilanzRead, PermissionType.BilanzWrite],
      'bilanzen-edit': [PermissionType.BilanzRead, PermissionType.BilanzWrite],
      nachweisverteilung: [PermissionType.Nachweisverteilung],
      pocs: [PermissionType.PocRead],
      'bewegungsdaten-import': [PermissionType.ImportInbound, PermissionType.ImportOutbound],
      import: [PermissionType.GenericImport],

      // Stammdaten
      allgemein: [PermissionType.MandantAllgemeinRead, PermissionType.MandantAllgemeinWrite],
      tauschpartner: [PermissionType.PartnerRead, PermissionType.PartnerWrite],
      'tauschpartner-edit': [PermissionType.PartnerRead, PermissionType.PartnerWrite],
      standorte: [PermissionType.StandortRead, PermissionType.StandortWrite],
      'standorte-edit': [PermissionType.StandortRead, PermissionType.StandortWrite],
      produkte: [PermissionType.ProduktRead, PermissionType.ProduktWrite],
      'produkte-edit': [PermissionType.ProduktRead, PermissionType.ProduktWrite],
      zertifizierungen: [
        PermissionType.ZertifizierungenRead,
        PermissionType.ZertifizierungenWrite
      ],
      'stammdaten-import': [
        PermissionType.ProduktWrite,
        PermissionType.StandortWrite,
        PermissionType.PartnerWrite
      ],

      // Reports
      'reports-liste': [PermissionType.Report],
      'reports-ytd': [PermissionType.YtdReport],
      auswertungen: [PermissionType.Report],

      // Dashboard ist für alle zugänglich
      dashboard: []
    };

    const requiredPermissions = routePermissions[routeName];

    // Wenn keine spezifischen Berechtigungen definiert sind, erlaube Zugriff
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    // Prüfe ob Benutzer mindestens eine der erforderlichen Berechtigungen hat
    return this.hasAnyPermission(requiredPermissions);
  }
}

export const permissionService = new PermissionService();
